import { Tabs } from '@ifood/pomodoro-components';
import { Location, WindowLocation, navigate } from '@reach/router';
import { useIntl } from 'gatsby-plugin-intl';
import React, { Children, FC, Fragment, useEffect, useMemo, useState } from 'react';

type RoutedTabsChildren = React.ReactElement<RoutedTabProps> | React.ReactElement<RoutedTabProps>[];

type InternalRoutedTabsProps = {
  children: RoutedTabsChildren;
  location: WindowLocation;
};

const formatPathLocale = (locale: string, path: string) => {
  return `/${locale}${path}`;
};

const InternalRoutedTabs: FC<InternalRoutedTabsProps> = ({ location, children }) => {
  const { locale } = useIntl();

  const tabsData = useMemo(
    () =>
      Children.toArray(children)
        .filter((node: React.ReactElement<RoutedTabProps>) => node.props.path)
        .map((node: React.ReactElement<RoutedTabProps>) => ({
          children: node.props.children,
          path: formatPathLocale(locale, node.props.path),
          Node: node,
        })),
    [children]
  );

  const urls = useMemo(() => tabsData.map((tab) => tab.path), [tabsData]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const findIndex = urls.findIndex((url) => url === location.pathname);

    if (findIndex === -1) {
      navigate(urls[0]);
      setIndex(0);
    } else {
      setIndex(findIndex);
    }
  }, [urls]);

  return (
    <>
      <Tabs
        activeIndex={index}
        onChange={(newIndex): void => {
          if (newIndex !== index) {
            setIndex(newIndex);
            navigate(urls[newIndex], { replace: false });
          }
        }}
      >
        {tabsData.map((tab) => tab.Node)}
      </Tabs>
      {tabsData.map((tab) => (
        <Fragment key={tab.path}>{location.pathname === tab.path && tab.children}</Fragment>
      ))}
    </>
  );
};

type RoutedTabsProps = {
  children: RoutedTabsChildren;
};

const RoutedTabs: FC<RoutedTabsProps> & TabsComposition = ({ children }) => (
  <Location>
    {({ location }): JSX.Element => <InternalRoutedTabs location={location}>{children}</InternalRoutedTabs>}
  </Location>
);

type RoutedTabProps = {
  path: string;
  label: React.ReactNode;
  children: React.ReactNode;
};

const RoutedTab: FC<RoutedTabProps> = ({ label }) => <Tabs.Pane label={label} />;

type TabsComposition = {
  Tab: typeof RoutedTab;
};

RoutedTabs.Tab = RoutedTab;

export { RoutedTabs };
